import { ChangeDetectorRef, Component, ElementRef } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FieldWrapper, FormlyFieldConfig } from "@ngx-formly/core";
import { Subscription } from "rxjs";
import { AsyncModalService } from "src/app/services/shared/async-modal-service";

@Component({
    standalone: false,
    templateUrl: "./paging.component.html",
    styleUrls: ["./paging.component.css"],
})
export class PagingComponent extends FieldWrapper {
    activeIndex: number = 0;
    pageIsValid = false;
    asyncModalService: AsyncModalService;
    cdr: ChangeDetectorRef;
    isRequired = false;
    showAllPageValidationMessage = false;
    route: ActivatedRoute;
    isDisabled = false;
    modifiedPageOrders: number[];
    private valueChangesSubscription: Subscription;

    constructor(
        cdr: ChangeDetectorRef,
        asyncModalService: AsyncModalService = null,
        route: ActivatedRoute,
        private elementRef: ElementRef<HTMLElement>
    ) {
        super();
        this.cdr = cdr;
        this.asyncModalService = asyncModalService;
        this.route = route;
        if (this.route.snapshot.data.state == "view") {
            this.isDisabled = true;
        }
    }

    ngOnInit() {
        this.modifiedPageOrders = [];
        this.setActiveIndex(this.activeIndex);
        this.field.props.getActivePageOrder = this.getActivePageOrder.bind(this);
        this.field.props.getModifiedPageOrders = this.getModifiedPageOrders.bind(this);
        this.field.props.getActivePageIndex = this.getActivePageOrder.bind(this);
        this.field.props.getModifiedPageIndexes = this.getModifiedPageOrders.bind(this);
        this.field.props.nextPage = this.nextPage.bind(this);
        this.field.props.hasPrevious = this.hasPrevious.bind(this);
        this.field.props.hasNext = this.hasNext.bind(this);
        this.field.props.goNext = this.goNext.bind(this);
        this.field.props.goPrevious = this.goPrevious.bind(this);
        this.field.props.pageCanProgress =
            this.pageCanProgress.bind(this);

        // As soon as the user changes something on the current page, show every other required
        // field on it that still needs an answer, rather than waiting until they try to leave.
        // Loading an entry also emits here, but leaves nothing dirty/touched, so it's ignored.
        this.valueChangesSubscription = this.form.valueChanges.subscribe(() => {
            setTimeout(() => {
                if (this.hasInteraction(this.field.fieldGroup?.[this.activeIndex]?.fieldGroup)) {
                    this.showErrorsOnCurrentPage(false);
                }
            });
        });
    }

    ngOnDestroy() {
        this.valueChangesSubscription?.unsubscribe();
    }

    ngDoCheck() {
        this.syncPageHiddenStates();
    }

    nextPage() {
        this.setActiveIndex(++this.activeIndex);
    }

    setActiveIndex(index: number) {
        this.activeIndex = index;

        if (!this.modifiedPageOrders.includes(index)) {
            this.modifiedPageOrders.push(index);
        }

        (this.field.fieldGroup || []).forEach((x) => {
            x.hide = true;
        });

        if ((this.field.fieldGroup || []).length > index)
            this.field.fieldGroup[this.activeIndex].hide = false;

        this.syncPageHiddenStates();
    }

    hasPrevious() {
        return this.activeIndex > 0;
    }

    hasNext() {
        return this.activeIndex < this.field.fieldGroup.length - 1;
    }

    goToPage(index: number) {
        // Once the user has interacted with the current page, block leaving it in either direction
        // until its required questions are filled. An untouched page can be left freely.
        // pageCanProgress() always passes in view mode, so read-only browsing is unaffected.
        if (
            index !== this.activeIndex &&
            this.hasInteraction(this.field.fieldGroup[this.activeIndex]?.fieldGroup) &&
            !this.pageCanProgress()
        ) {
            this.showErrorsOnCurrentPage();
            return;
        }

        if (
            index !== this.activeIndex &&
            !this.field.props.isPreview &&
            !this.field.props.isDisabled
        ) {
            this.field.props.savePage(this.activeIndex);
            this.field.props.setPlantOptions();
        }
        this.field.props.checkLoop(
            this.field.fieldGroup[index].fieldGroup
        );

        document.getElementById("mainHeader").scrollIntoView();
        this.setActiveIndex(index);
        this.cdr.detectChanges();
    }

    // Replaces the old "please complete this page" popups: flags every field on the
    // current page red at once and, when blocking navigation, scrolls to the first one.
    private showErrorsOnCurrentPage(scrollToFirstError: boolean = true) {
        const page = this.field.fieldGroup?.[this.activeIndex];

        if (!page || this.isDisabled) {
            return;
        }

        // Touch fields individually - a page has no form key of its own, so its
        // formControl is the whole form and markAllAsTouched on it would flag every page.
        this.touchFields(page.fieldGroup);

        // Field types are OnPush and touching a control emits no event, so without this
        // the red state only appeared once the user clicked into each field.
        this.field.options.detectChanges?.(page);
        this.cdr.detectChanges();

        if (!scrollToFirstError) {
            return;
        }

        setTimeout(() => {
            const firstError = this.elementRef.nativeElement.querySelector(
                ".is-invalid, .field-has-error, .has-error, .invalid-feedback"
            );

            firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    }

    // Logic hiding is recorded in three places: field.hide (Formly, from hideExpression),
    // props.isLogicallyHidden (qa.form's showHideField) and props.isParentLogicallyHidden
    // (set here when a whole sub page is hidden).
    private isHidden(field: FormlyFieldConfig): boolean {
        return !!(
            field.hide ||
            field.props?.hidden ||
            field.props?.isLogicallyHidden ||
            field.props?.isParentLogicallyHidden
        );
    }

    // A page counts as interacted with once the user has changed any field (dirty) or clicked into
    // a required one (touched) - e.g. opening a required Date picker and closing it empty.
    // Touched on optional fields doesn't count, and loading an entry marks nothing dirty/touched.
    private hasInteraction(fields: FormlyFieldConfig[] = []): boolean {
        return fields.some((field) => {
            if (!field || this.isHidden(field)) {
                return false;
            }

            if (field.fieldGroup?.length) {
                return this.hasInteraction(field.fieldGroup);
            }

            return !!(
                field.formControl?.dirty ||
                (field.formControl?.touched && field.props?.required)
            );
        });
    }

    private touchFields(fields: FormlyFieldConfig[] = []) {
        for (const field of fields) {
            if (!field || this.isHidden(field)) {
                continue;
            }

            if (field.fieldGroup?.length) {
                this.touchFields(field.fieldGroup);
            } else {
                field.formControl?.markAsTouched();
            }
        }
    }

    goPrevious() {
        if (this.activeIndex > 0) this.goToPage(this.activeIndex - 1);
    }

    goNext() {
        if (this.activeIndex < this.field.fieldGroup.length - 1)
            this.goToPage(this.activeIndex + 1);
    }

    pageCanProgress(parent?: any) {
        var canProgress = true;
        if (!this.isDisabled) {
            this.isRequired = false;
            this.showAllPageValidationMessage = false;

            var formlyConfig = parent
                ? parent
                : this.field.fieldGroup[this.activeIndex];

            if (formlyConfig && formlyConfig.fieldGroup) {
                for (
                    let i = 0;
                    i < formlyConfig.fieldGroup.length && canProgress;
                    i++
                ) {

                    let question = formlyConfig.fieldGroup[i];

                    // A question hidden by logic (or inside a hidden rollup/sub page) can't be
                    // answered, so it must not block the page - skip it and everything inside it.
                    if (this.isHidden(question)) {
                        continue;
                    }

                    if (question.fieldGroup) {
                        if (question.props.required) {
                            canProgress = false;
                            this.isRequired = true;
                        }
                        else if (question.type == "hz-select" && question.props.options.length == 0) {
                            continue;
                        }
                        else {
                            canProgress = this.pageCanProgress(question);
                        }
                    }
                    else if (
                        question.props.requiredPageChange &&
                        ((question.type != "file-upload" && !(formlyConfig.model[question.key as string])) || (question.type == "file-upload" && !(formlyConfig.model[question.key].length > 0))) &&
                        !question.isViewOnlyInEdit &&
                        !(question.props.disabled || question.props.hidden)
                    ) {
                        if (question.type == "hz-select" && question.props.options.length == 0) {
                            continue;
                        }
                        else if (question.type == "table"
                        ) {
                            if (formlyConfig.model[question.key as string] && formlyConfig.model[question.key as string].length > 0) {
                                if ((!formlyConfig.model[question.key][0].item || formlyConfig.model[question.key][0].item.trim() == "") ||
                                    (!formlyConfig.model[question.key][0].assetNo || formlyConfig.model[question.key][0].assetNo.trim() == "") ||
                                    (!formlyConfig.model[question.key][0].description || formlyConfig.model[question.key][0].description.trim() == "") ||
                                    (!formlyConfig.model[question.key][0].manufacturer || formlyConfig.model[question.key][0].manufacturer.trim() == "") ||
                                    (!formlyConfig.model[question.key][0].modelNo || formlyConfig.model[question.key][0].modelNo.trim() == "") ||
                                    (!formlyConfig.model[question.key][0].owner || formlyConfig.model[question.key][0].owner.trim() == "")) {
                                    canProgress = false;
                                    this.isRequired = question.isRequired;

                                }
                            }
                            else {
                                canProgress = false;
                                this.isRequired = question.isRequired;
                            }
                        }

                        canProgress = false;
                        this.showAllPageValidationMessage = true;
                    }
                    else if (
                        question.props.required &&
                        (!question.isViewOnlyInEdit && !(question.props.disabled || question.props.hidden))
                    ) {
                        let isArray = Array.isArray(formlyConfig.model[question.key as string]);
                        if ((!isArray && !(formlyConfig.model[question.key as string])) || (isArray && !(formlyConfig.model[question.key].length > 0))) {
                            canProgress = false;
                            this.isRequired = true;
                        }
                    }
                    else if (question.type == "table" && question.isRequired && !(question.props.disabled || question.props.hidden)) {
                        if (formlyConfig.model[question.key as string] && formlyConfig.model[question.key as string].length > 0) {
                            if ((!formlyConfig.model[question.key][0].item || formlyConfig.model[question.key][0].item.trim() == "") ||
                                (!formlyConfig.model[question.key][0].assetNo || formlyConfig.model[question.key][0].assetNo.trim() == "") ||
                                (!formlyConfig.model[question.key][0].description || formlyConfig.model[question.key][0].description.trim() == "") ||
                                (!formlyConfig.model[question.key][0].manufacturer || formlyConfig.model[question.key][0].manufacturer.trim() == "") ||
                                (!formlyConfig.model[question.key][0].modelNo || formlyConfig.model[question.key][0].modelNo.trim() == "") ||
                                (!formlyConfig.model[question.key][0].owner || formlyConfig.model[question.key][0].owner.trim() == "")) {
                                canProgress = false;
                                this.isRequired = question.isRequired;
                            }
                        }
                        else {
                            canProgress = false;
                            this.isRequired = question.isRequired;
                        }
                        if (!canProgress && question.props.requiredPageChange) {
                            this.showAllPageValidationMessage = true;
                        }
                    }
                    // if all question on 'page' are mandatory
                    else if (
                        formlyConfig.props.required &&
                        (
                            (
                                //if 'Other' option is selected in dropdown and 'Other' textbox is empty
                                question.key != undefined &&
                                question.key.indexOf("_other") > -1 &&
                                formlyConfig.model[question.key.split("_other")[0]] == "Other" &&
                                !formlyConfig.model[question.key as string] &&
                                question.type == "hz-select"
                            ) ||
                            // if question values is empty
                            (
                                question.key != undefined &&
                                question.key.indexOf("_other") == -1 &&
                                !formlyConfig.model[question.key as string] &&
                                question.type != "note" &&
                                question.key.indexOf('_signature_print') == -1 &&
                                (
                                    (question.type == "hz-input" && question.extendedType != "Normal" && question.extendedType != 0) ||
                                    question.type == "hz-textarea"
                                )
                            )
                        ) &&
                        canProgress) {
                        canProgress = false;
                        this.showAllPageValidationMessage = true;
                    }
                }
            }
        }
        return canProgress;
    }

    getModifiedPageOrders() {
        let modifiedPageOrders = [];
        (this.modifiedPageOrders || []).forEach((modifiedPageIndex) => {
            if (this.field.wrappers.indexOf("paging") > -1 && this.field.fieldGroup[modifiedPageIndex]) {
                modifiedPageOrders.push((this.field as any).fieldGroup[modifiedPageIndex].fieldGroup[0].order);
            }
        });
        return modifiedPageOrders;
    }

    isPageHidden(index: number, applyHiddenState: boolean = false) {
        if (this.field.wrappers.indexOf("paging") > -1 && this.field.fieldGroup[index]) {
            let hideExpression = this.field.fieldGroup[index].props.hideExpression;

            if (hideExpression != "") {
                hideExpression = hideExpression.replaceAll('model', 'this.field.fieldGroup[index].model');
                if (eval(hideExpression)) {
                    if (applyHiddenState) {
                        this.showHideSubpageChildFields(this.field.fieldGroup[index], true);
                    }
                    return true;
                }
                else {
                    if (applyHiddenState) {
                        this.showHideSubpageChildFields(this.field.fieldGroup[index], false);
                    }
                    return false;
                }
            }
            else {
                if (applyHiddenState) {
                    this.showHideSubpageChildFields(this.field.fieldGroup[index], false);
                }
                return false;
            }
        }

        return false;
    }

    getActivePageOrder() {
        if (this.field.wrappers.indexOf("paging") > -1 && this.field.fieldGroup[this.activeIndex]) {
            return (this.field as any).fieldGroup[this.activeIndex].fieldGroup[0].order;
        }
    }

    private getField(
        key: string,
        fieldGroup: Array<FormlyFieldConfig> = null,
        additionalField: string = null
    ): FormlyFieldConfig {
        if (additionalField != null) {
            return this.getFieldByProperty(additionalField, key, fieldGroup);
        } else {
            return this.getFieldByProperty("key", key, fieldGroup);
        }
    }

    private getFieldByProperty(
        property: string,
        value: any,
        fieldGroup: Array<FormlyFieldConfig> = null
    ): FormlyFieldConfig {
        const fields = fieldGroup || this.field.fieldGroup;
        if (!fields) return null;

        for (let field of fields) {
            if (field.fieldGroup) {
                const result = this.getField(value, field.fieldGroup, property);
                if (result) {
                    return result;
                }
                else if (field[property] == value) {
                    return field;
                }
            } else if (field[property] == value) {
                return field;
            }
        }
    }

    private showHideSubpageChildFields(
        subPageFieldGroup: FormlyFieldConfig,
        isFieldHidden: boolean
    ): FormlyFieldConfig | null {
        const fields = subPageFieldGroup.fieldGroup;
        if (!fields) return null;

        for (let field of fields) {
            if (field.fieldGroup) {
                if (field.wrappers.includes("grid")) {
                    continue;
                }
                const result = this.showHideSubpageChildFields(field, isFieldHidden);
                if (result) {
                    return result;
                }
                else {
                    field.props.isParentLogicallyHidden = isFieldHidden;
                    continue;
                }
            } else {
                field.props.isParentLogicallyHidden = isFieldHidden
                continue;
            }
        }
    }

    private syncPageHiddenStates() {
        (this.field.fieldGroup || []).forEach((page, index) => {
            if (page) {
                this.isPageHidden(index, true);
            }
        });
    }
}
